import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import type { Mode } from '@confluence/macro-tracker/entry-points/types.ts';

import {
	usePopularLabelsData,
	usePopularLabelsDataForSpace,
} from './hooks/usePopularLabelsData.ts';
import { i18n } from './popularLabelsMacroI18n.ts';
import { ListLayout } from './ListLayout.tsx';
import { HeatmapLayout } from './HeatmapLayout.tsx';
import type { PopularLabelData, StyleType } from './popularLabelsTypes.ts';
import { parseCount, parseStyle, resolveSpaceKey } from './utils/popularLabelsParamsUtils.ts';

type PopularLabelsMacroMode = ExtensionHandlerProps['mode'] | 'live';

export type PopularLabelsMacroRendererProps = {
	macroDefaultProps: Omit<ExtensionHandlerProps, 'mode'> & {
		mode: PopularLabelsMacroMode;
	};
	macroParams?: {
		count?: { value?: string };
		spaceKey?: { value?: string };
		style?: { value?: string };
		/**
		 * The space key of the page hosting the macro. Injected by the
		 * extension handler so we can resolve the legacy `'currentSpace()'`
		 * sentinel that authors / templates may use as the `spaceKey` param.
		 */
		currentSpaceKey?: string;
	};
};

type PopularLabelsViewProps = {
	trackingAttributes: NonNullable<
		React.ComponentProps<typeof MacroExperienceSuccess>['attributes']
	>;
	trackingContentId: string;
	trackingExtensionKey: string | undefined;
	trackingMode: Mode;
	trackingName: string;
	macroId: string | undefined;
	spaceKey: string | undefined;
	layoutStyle: StyleType;
	labels: PopularLabelData[] | undefined;
	loading: boolean;
	error: Error | null;
	/**
	 * When set, label links render with `target` so they open in a new tab.
	 * Set to `_blank` in editor and live modes — view mode preserves the
	 * legacy same-tab navigation so click → page navigation feels native.
	 */
	linkTarget: '_blank' | undefined;
};

const PopularLabelsView = ({
	trackingAttributes,
	trackingContentId,
	trackingExtensionKey,
	trackingMode,
	trackingName,
	macroId,
	spaceKey,
	layoutStyle,
	labels,
	loading,
	error,
	linkTarget,
}: PopularLabelsViewProps) => {
	const { formatMessage } = useIntl();
	const errorMessage = formatMessage(i18n.error);

	if (loading) {
		return null;
	}

	if (error) {
		return (
			<>
				<MacroExperienceFailure
					name={trackingName}
					contentId={trackingContentId}
					extensionKey={trackingExtensionKey}
					mode={trackingMode}
					attributes={trackingAttributes}
					error={error}
				/>
				<ErrorDisplay error={error}>
					<Box testId="popular-labels-error-state">{errorMessage}</Box>
				</ErrorDisplay>
			</>
		);
	}

	if (!labels) {
		const labelsError = new Error(formatMessage(i18n.error));
		return (
			<>
				<MacroExperienceFailure
					name={trackingName}
					contentId={trackingContentId}
					extensionKey={trackingExtensionKey}
					mode={trackingMode}
					attributes={trackingAttributes}
					error={labelsError}
				/>
				<ErrorDisplay error={labelsError}>
					<Box testId="popular-labels-error-state">{errorMessage}</Box>
				</ErrorDisplay>
			</>
		);
	}

	if (labels.length === 0) {
		return (
			<>
				<MacroExperienceSuccess
					name={trackingName}
					contentId={trackingContentId}
					extensionKey={trackingExtensionKey}
					mode={trackingMode}
					attributes={trackingAttributes}
				/>
				<Box testId="popular-labels-empty-state">{formatMessage(i18n.noLabelsFound)}</Box>
			</>
		);
	}

	const LayoutComponent = layoutStyle === 'heatmap' ? HeatmapLayout : ListLayout;

	return (
		<>
			<MacroExperienceSuccess
				name={trackingName}
				contentId={trackingContentId}
				extensionKey={trackingExtensionKey}
				mode={trackingMode}
				attributes={trackingAttributes}
			/>
			<Box testId="popular-labels-macro" data-fabric-macro={macroId} data-macro-body="">
				<LayoutComponent labels={labels} spaceKey={spaceKey} linkTarget={linkTarget} />
			</Box>
		</>
	);
};

type PopularLabelsDataProps = {
	trackingAttributes: NonNullable<
		React.ComponentProps<typeof MacroExperienceSuccess>['attributes']
	>;
	trackingContentId: string;
	trackingExtensionKey: string | undefined;
	trackingMode: Mode;
	trackingName: string;
	macroId: string | undefined;
	first: number;
	layoutStyle: StyleType;
	linkTarget: '_blank' | undefined;
};

const GlobalPopularLabelsRenderer = ({
	trackingAttributes,
	trackingContentId,
	trackingExtensionKey,
	trackingMode,
	trackingName,
	macroId,
	first,
	layoutStyle,
	linkTarget,
}: PopularLabelsDataProps) => {
	const {
		data: labels,
		loading,
		error,
	} = usePopularLabelsData({
		spaceId: null,
		first,
		style: layoutStyle,
	});
	return (
		<PopularLabelsView
			trackingAttributes={trackingAttributes}
			trackingContentId={trackingContentId}
			trackingExtensionKey={trackingExtensionKey}
			trackingMode={trackingMode}
			trackingName={trackingName}
			macroId={macroId}
			spaceKey={undefined}
			layoutStyle={layoutStyle}
			labels={labels}
			loading={loading}
			error={error}
			linkTarget={linkTarget}
		/>
	);
};

const SpaceScopedPopularLabelsRenderer = ({
	trackingAttributes,
	trackingContentId,
	trackingExtensionKey,
	trackingMode,
	trackingName,
	macroId,
	first,
	layoutStyle,
	spaceKey,
	linkTarget,
}: PopularLabelsDataProps & { spaceKey: string }) => {
	const {
		data: labels,
		loading,
		error,
	} = usePopularLabelsDataForSpace({
		spaceKey,
		first,
		style: layoutStyle,
	});
	return (
		<PopularLabelsView
			trackingAttributes={trackingAttributes}
			trackingContentId={trackingContentId}
			trackingExtensionKey={trackingExtensionKey}
			trackingMode={trackingMode}
			trackingName={trackingName}
			macroId={macroId}
			spaceKey={spaceKey}
			layoutStyle={layoutStyle}
			labels={labels}
			loading={loading}
			error={error}
			linkTarget={linkTarget}
		/>
	);
};

const PopularLabelsMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: PopularLabelsMacroRendererProps) => {
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const trackingMode: Mode = mode === 'live' ? 'renderer' : mode;
	const trackingAttributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const trackingName = getExperienceName(trackingMode, node);
	const count = parseCount(macroParams?.count?.value);
	const style = parseStyle(macroParams?.style?.value);
	// Resolve the legacy `'currentSpace()'` sentinel to the host page's space
	// key before deciding between the global vs. space-scoped renderer.
	const spaceKey = resolveSpaceKey(macroParams?.spaceKey?.value, macroParams?.currentSpaceKey);
	// In view mode (`renderer`) labels behave like normal page links and
	// navigate in place. In authoring surfaces we open them in a new tab so
	// authors don't lose their unsaved draft when inspecting a label.
	const linkTarget: '_blank' | undefined =
		macroDefaultProps?.mode === 'editor' || macroDefaultProps?.mode === 'live'
			? '_blank'
			: undefined;
	const macroId =
		typeof macroDefaultProps?.attributes?.macroId === 'string'
			? macroDefaultProps.attributes.macroId
			: undefined;

	if (spaceKey) {
		return (
			<SpaceScopedPopularLabelsRenderer
				trackingAttributes={trackingAttributes}
				trackingContentId={contentId}
				trackingExtensionKey={extensionKey}
				trackingMode={trackingMode}
				trackingName={trackingName}
				macroId={macroId}
				first={count}
				layoutStyle={style}
				spaceKey={spaceKey}
				linkTarget={linkTarget}
			/>
		);
	}

	return (
		<GlobalPopularLabelsRenderer
			trackingAttributes={trackingAttributes}
			trackingContentId={contentId}
			trackingExtensionKey={extensionKey}
			trackingMode={trackingMode}
			trackingName={trackingName}
			macroId={macroId}
			first={count}
			layoutStyle={style}
			linkTarget={linkTarget}
		/>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

export const PopularLabelsMacroRenderer = (
	props: PopularLabelsMacroRendererProps,
): React.JSX.Element => {
	return (
		<GenericErrorBoundary
			renderOnError={renderErrorFallback}
			attribution={Attribution.PAGE_EXPERIENCES}
		>
			<PopularLabelsMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
